import { DEFAULT_COUNTRY, DEFAULT_LANGUAGE, COUNTRY_OPTIONS, LANGUAGE_OPTIONS } from '../../constants/geoTargeting';
import { readConsent } from '../../utils/consent';
import type { MonitoringProject } from '../../api/monitoring';

export type GuidedTask = 'briefing' | 'monitoring';
export interface OnboardingState {
  task: GuidedTask | null;
  keyword: string;
  website: string;
  projectId: string;
  country: string;
  language: string;
  frequency: 'daily' | 'weekly';
  startedAt: number | null;
  submittedAt: number | null;
  resultAt: number | null;
  dismissed: boolean;
}

export const emptyOnboarding = (): OnboardingState => ({
  task: null, keyword: '', website: '', projectId: '', country: DEFAULT_COUNTRY,
  language: DEFAULT_LANGUAGE, frequency: 'daily', startedAt: null, submittedAt: null,
  resultAt: null, dismissed: false,
});
const memoryFallback = new Map<string, OnboardingState>();
const keyFor = (userId: string, orgId: string) => `lazysem_onboarding_v1:${encodeURIComponent(userId)}:${encodeURIComponent(orgId)}`;

export function readOnboarding(userId: string, orgId: string): OnboardingState {
  const key = keyFor(userId, orgId);
  const fallback = memoryFallback.get(key);
  if (fallback) return { ...fallback };
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    if (!saved || typeof saved !== 'object') return emptyOnboarding();
    const value = emptyOnboarding();
    value.task = saved.task === 'briefing' || saved.task === 'monitoring' ? saved.task : null;
    for (const field of ['keyword', 'website', 'projectId'] as const) {
      if (typeof saved[field] === 'string') value[field] = saved[field].slice(0, field === 'website' ? 2048 : 500);
    }
    if (COUNTRY_OPTIONS.some(c => c.value === saved.country)) value.country = saved.country;
    if (LANGUAGE_OPTIONS.some(l => l.value === saved.language)) value.language = saved.language;
    if (saved.frequency === 'weekly') value.frequency = 'weekly';
    for (const field of ['startedAt', 'submittedAt', 'resultAt'] as const) {
      if (typeof saved[field] === 'number' && Number.isFinite(saved[field]) && saved[field] > 0) value[field] = saved[field];
    }
    value.dismissed = saved.dismissed === true;
    return value;
  } catch { return emptyOnboarding(); }
}

export function saveOnboarding(userId: string, orgId: string, value: OnboardingState): void {
  const key = keyFor(userId, orgId);
  try { localStorage.setItem(key, JSON.stringify(value)); memoryFallback.delete(key); }
  catch { memoryFallback.set(key, { ...value }); }
}

/** Empty is allowed for optional websites; null means invalid. */
export function normalizeTaskUrl(raw: string): string | null {
  const text = raw.trim();
  if (!text) return '';
  if (/\s/.test(text)) return null;
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(text) ? text : `https://${text}`);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || !url.hostname.includes('.')) return null;
    return url.href;
  } catch { return null; }
}

export function buildGuidedPrompt(draft: OnboardingState, projectName?: string): string {
  const review = 'Walk me through the next steps and show the estimated credits for my approval before starting. When it’s ready, link me to the result.';
  const website = normalizeTaskUrl(draft.website) || '';
  if (draft.task === 'monitoring') {
    return `Help me monitor only this page: ${website}\nCheck frequency: ${draft.frequency}. Reuse a matching project if I already have one.\n${review}`;
  }
  const country = COUNTRY_OPTIONS.find(c => c.value === draft.country)?.label || draft.country;
  const language = LANGUAGE_OPTIONS.find(l => l.value === draft.language)?.label || draft.language;
  return `Help me create a content brief about “${draft.keyword.trim()}”.\n${website ? `Website: ${website}\n` : ''}Target country: ${country}. Content language: ${language}.\n${draft.projectId ? `Use my existing project: ${projectName || draft.projectId}.` : 'Help me choose or create the right project.'}\n${review}`;
}

export function trackOnboarding(stage: 'task_selected' | 'details_supplied' | 'first_result', task: GuidedTask, elapsedSeconds?: number): void {
  if (readConsent()?.analytics_storage !== 'granted') return;
  const target = window as unknown as { dataLayer?: Record<string, unknown>[] };
  target.dataLayer = target.dataLayer || [];
  target.dataLayer.push({ event: `onboarding_${stage}`, task,
    ...(elapsedSeconds === undefined ? {} : { elapsed_seconds: Math.max(0, Math.round(elapsedSeconds)) }),
  });
}

export function hasMonitoringResult(draft: OnboardingState, projects: Pick<MonitoringProject, 'specificUrls' | 'scopeType' | 'lastCheck' | 'lastError' | 'crawlStatus'>[]): boolean {
  const requestedUrl = normalizeTaskUrl(draft.website);
  if (!requestedUrl || !draft.submittedAt) return false;
  return projects.some(project => !project.lastError
    && ['completed', 'done'].includes(project.crawlStatus?.toLowerCase() || '')
    && Date.parse(project.lastCheck || '') >= draft.submittedAt!
    // Project timestamps describe all pages. Only a single-page scope is sufficient evidence here.
    && project.scopeType === 'specific_urls'
    && project.specificUrls?.length === 1
    && normalizeTaskUrl(project.specificUrls[0]) === requestedUrl);
}
